import { FC, useCallback, useRef } from "react";
import { MenuProps } from "react-select";

import { AdjustmentBounds, ElementBounds, Overlay } from "capitalrx-components-2.0/lib/general";
import { createComponentStyleHook } from "capitalrx-components-2.0/lib/tokens";
import classNames from "classnames";

import { Bounds } from "../../../../utilities/BoundsObserver/Bounds";
import { ValidityGroup } from "../../../ValidityGroup";
import { OptionItem } from "../../OptionItem";
import { getStyleModeSafeProps } from "../../../utils/getStyleModeSafeProps";

import tokenStyles from "./styles.module.css";
import legacyStyles from "./styles.module.scss";

const useResolvedDropdownMenuStyles = createComponentStyleHook<MenuProps<OptionItem | null>>({
  legacyStyles,
  tokenStyles,
  componentTokens: "dropdown_menu",
});

/**
 * The menu component.
 */
export const Menu: FC<MenuProps<OptionItem | null>> = (props) => {
  const { children, innerProps, innerRef } = props;
  const { mode, styles, StyleModeBoundary } = useResolvedDropdownMenuStyles(getStyleModeSafeProps(props));
  const menuContainerRef = useRef<HTMLDivElement | null>(null);

  const menuClassName = classNames(styles.Menu, "menu");

  const adjustOverlay = useCallback((elementBounds: ElementBounds, containerBounds: Bounds): AdjustmentBounds => {
    const { bounds } = elementBounds;
    if (bounds.top + bounds.height <= containerBounds.height) return bounds;

    return {
      top: bounds.top - bounds.height - (menuContainerRef.current?.offsetTop ?? 0),
      left: "auto",
    };
  }, []);

  return (
    <StyleModeBoundary mode={mode}>
      <div ref={menuContainerRef} className={styles.MenuContainer}>
        <Overlay adjust={adjustOverlay}>
          <div {...innerProps} ref={innerRef} className={menuClassName} role="presentation">
            <ValidityGroup ignoreParent invalid={false} error={null}>
              {children}
            </ValidityGroup>
          </div>
        </Overlay>
      </div>
    </StyleModeBoundary>
  );
};
